<template>
  <div class="container-fluid py-4 statistics-view">
    <div v-if="isLoading" class="loading-overlay">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Caricamento...</span>
      </div>
      <div class="loading-text">Caricamento in corso...</div>
    </div>

    <div class="row mb-4">
      <div class="col-12 col-md-6"></div>

      <div class="col-12 col-md-6 d-flex gap-2 justify-content-end flex-wrap">

        <!-- Articoli -->
        <div class="dropdown filter-dropdown">
          <button class="btn btn-light border text-dark dropdown-toggle article-btn" data-bs-toggle="dropdown">
            {{ selectedArticlesLabel }}
          </button>
          <ul class="dropdown-menu p-2" style="max-height: 250px; overflow-y:auto;">
            <li class="mb-2 px-2">
              <input type="text" class="form-control form-control-sm" placeholder="Cerca..." v-model="articleSearch">
            </li>
            <li v-for="a in filteredArticles" :key="a.id" class="form-check">
              <input class="form-check-input" type="checkbox" :value="a.id" v-model="selectedArticlesIds">
              <label class="form-check-label ms-2">
                {{ a.versions[a.versions.length - 1].title }}
              </label>
            </li>
          </ul>
        </div>

        <!-- Regioni -->
        <div class="dropdown filter-dropdown">
          <button class="btn btn-light border text-dark dropdown-toggle" data-bs-toggle="dropdown">
            {{ selectedRegionsLabel }}
          </button>
          <ul class="dropdown-menu p-2" style="max-height: 250px; overflow-y:auto;">
            <li v-for="r in allRegions" :key="r.id" class="form-check">
              <input class="form-check-input" type="checkbox" :value="r.id" v-model="filterRegion">
              <label class="form-check-label ms-2">{{ r.name }}</label>
            </li>
          </ul>
        </div>

        <!-- Anno o periodo personalizzato -->
        <div class="dropdown filter-dropdown">
          <select v-model="selectedPeriodType" @change="onPeriodTypeChange" class="form-select form-select-sm">
            <option v-for="y in dynamicYears" :key="y" :value="y">{{ y }}</option>
            <option value="custom">Periodo Personalizzato</option>
          </select>
        </div>

        <div v-if="selectedPeriodType === 'custom'" class="d-flex gap-1">
          <input type="date" class="form-control form-control-sm" v-model="dateFrom" />
          <input type="date" class="form-control form-control-sm" v-model="dateTo" />
        </div>

        <button class="btn btn-primary filter-btn" title="Applica filtri" @click="fetchStats" :disabled="selectedPeriodType === 'custom' && (!dateFrom || !dateTo)">
          <i class="bi bi-search"></i>
        </button>

        <button class="btn btn-outline-secondary filter-btn" title="Reset filtri" @click="resetFilters">
          <i class="bi bi-x-lg"></i>
        </button>

        <div class="dropdown">
          <button class="btn btn-outline-secondary btn-sm dropdown-toggle" data-bs-toggle="dropdown">
            <i class="bi bi-download me-1"></i> Esporta
          </button>
          <ul class="dropdown-menu dropdown-menu-end">
            <li><a class="dropdown-item" @click="exportPNG">PNG</a></li>
            <li><a class="dropdown-item" @click="exportCSV">CSV</a></li>
            <li><a class="dropdown-item" @click="exportExcelWithChart">XLSX</a></li>
          </ul>
        </div>
      </div>
    </div>

    <!-- ================= CARD 1: Statistiche Quesiti + Grafico ================= -->
    <div class="card shadow-sm mb-4">
      <div class="card-body">
        <h5 class="fw-semibold mb-3">Statistiche Quesiti</h5>

        <!-- KPI full width -->
        <div class="d-flex mb-3 gap-2">
          <div v-for="kpi in kpiTopComputed" :key="kpi.label" class="text-white rounded p-3 text-center"
            :style="{ backgroundColor: kpi.bg, flex: 1 }">
            <div class="fw-bold fs-4">{{ kpi.value }}</div>
            <div class="small">{{ kpi.label }}</div>
          </div>
        </div>

        <!-- Toggle grafico -->
        <div class="d-flex justify-content-center gap-2 mt-4 mb-3">
          <button class="btn chart-toggle" :class="{ active: chartMode === 'bar' }" @click="chartMode = 'bar'"
            title="Grafico a barre">
            <i class="bi bi-bar-chart-fill"></i>
          </button>
          <button class="btn chart-toggle" :class="{ active: chartMode === 'line' }" @click="chartMode = 'line'"
            title="Grafico a linee">
            <i class="bi bi-graph-up"></i>
          </button>
        </div>

        <!-- Grafico -->
        <VChart ref="chartRef" :option="chartOptions" style="height: 380px; width: 100%" />
      </div>
    </div>

    <!-- ================= CARD 3: Tempo medio ================= -->
    <div class="card shadow-sm">
      <div class="card-body">
        <h5 class="fw-semibold mb-4">Tempo di gestione medio</h5>
        <div v-for="barra in tempiGestione" :key="barra.label" class="mb-3">
          <div class="d-flex justify-content-between mb-1">
            <span>{{ barra.label }}</span>
            <span class="fw-semibold">{{ barra.value }} {{ barra.unit }}</span>
          </div>
          <div class="progress" style="height: 14px">
            <div class="progress-bar" role="progressbar"
              :style="{ width: (barra.value / barra.max * 100) + '%', backgroundColor: barra.color }">
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'

export default {
  name: 'StatisticsUOCView',
  data() {
    const currentYear = new Date().getFullYear();
    return {
      // dati grafico
      nuovi: [],
      assegnati: [],
      asseverati: [],
      nonAsseverati: [],
      graficoLabels: [],

      // KPI
      kpiTop: [],
      articoloRilevanteId: null,

      // tempi medi
      tempiGestione: [],

      chartMode: 'line',
      isLoading: true,

      // filtri
      selectedExperts: [],
      selectedArticlesIds: [],
      articleSearch: '',
      filterRegion: [],
      selectedYear: currentYear,
      selectedPeriodType: currentYear,
      dateFrom: null,
      dateTo: null,
      dynamicYears: Array.from({ length: 5 }, (_, i) => currentYear - i),

      // dati master
      allExperts: [],
      availableArticles: [],
      allRegions: [],

      countdownSettings: {
        fourth_column_days: 1,
        third_column_days: 3
      }
    }
  },
  computed: {
    kpiTopComputed() {
      return this.kpiTop.map(kpi => {
        if (kpi.key === 'articolo') {
          return {
            ...kpi,
            value: this.articoloRilevanteId
              ? this.articoloRilevanteTitle
              : 'N/A'
          };
        }
        return kpi;
      });
    },
    articoloRilevanteTitle() {
      const articolo = this.availableArticles.find(
        a => a.id === this.articoloRilevanteId
      );

      if (!articolo) return 'N/A';

      let title = articolo.versions.at(-1).title;

      title = title.split('(')[0].trim();

      return title;
    },
    filteredArticles() {
      let articles = this.availableArticles.slice();
      if (this.articleSearch) {
        articles = articles.filter(a =>
          this.formatArticleTitle(a.versions[a.versions.length - 1].title)
            .toLowerCase()
            .includes(this.articleSearch.toLowerCase())
        );
      }
      return articles.sort((a, b) => a.id - b.id);
    },
    selectedArticlesLabel() {
      const count = this.selectedArticlesIds.length;
      if (count === 0) return 'Articoli';
      if (count === 1) {
        const article = this.availableArticles.find(a => a.id === this.selectedArticlesIds[0]);
        return article ? article.versions[article.versions.length - 1].title : 'Articolo';
      }
      return `Selezione multipla (${count})`;
    },
    selectedRegionsLabel() {
      const count = this.filterRegion.length;
      if (count === 0) return 'Regioni';
      if (count === 1) return this.allRegions.find(r => r.id === this.filterRegion[0])?.name || 'Regione';
      return `Selezione multipla (${count})`;
    },
    chartOptions() {
      const isBar = this.chartMode === 'bar';
      return {
        tooltip: { trigger: 'axis' },
        legend: { data: ['Nuovi', 'Assegnati', 'Asseverati', 'Non Asseverati'] },
        xAxis: { type: 'category', data: this.graficoLabels },
        yAxis: { type: 'value', min: 0, max: 'dataMax' },
        series: [
          { name: 'Nuovi', type: isBar ? 'bar' : 'line', data: this.nuovi, itemStyle: { color: '#0dcaf0' } },
          { name: 'Assegnati', type: isBar ? 'bar' : 'line', data: this.assegnati, itemStyle: { color: '#ffc107' } },
          { name: 'Asseverati', type: isBar ? 'bar' : 'line', data: this.asseverati, itemStyle: { color: '#198754' } },
          { name: 'Non Asseverati', type: isBar ? 'bar' : 'line', smooth: true, data: this.nonAsseverati, itemStyle: { color: '#dc3545' } }
        ]
      }
    }
  },
  mounted() {
    this.loadAllExperts();
    this.loadAllRegions();
    this.fetchStats();
  },
  watch: {
    '$root.contents.articles': {
      immediate: true,
      handler(val) {
        if (val && Object.keys(val).length) {
          this.loadArticles();
          this.fetchStats();
        }
      }
    },
    dateFrom() {
      if (this.selectedPeriodType === 'custom' && this.dateFrom && this.dateTo) {
        //this.fetchStats()
      }
    },
    dateTo() {
      if (this.selectedPeriodType === 'custom' && this.dateFrom && this.dateTo) {
        //this.fetchStats()
      }
    }
  },
  methods: {
    resetFilters() {
      this.selectedExperts = [];
      this.selectedArticlesIds = [];
      this.filterRegion = [];
      const currentYear = new Date().getFullYear();
      this.selectedYear = currentYear;
      this.selectedPeriodType = currentYear;
      this.dateFrom = null;
      this.dateTo = null;
      this.fetchStats();
    },
    onPeriodTypeChange() {
      if (this.selectedPeriodType !== 'custom') {
        this.dateFrom = null;
        this.dateTo = null;
        this.selectedYear = this.selectedPeriodType;
        //this.fetchStats();
      }
    },
    async loadAllExperts() {
      const res = await this.$root.$api.get('/users/legal');
      this.allExperts = res.data;
    },
    async loadAllRegions() {
      const res = await this.$root.$api.get('/questions/regions');
      this.allRegions = res.data;
    },
    loadArticles() {
      this.availableArticles = this.extractAllArticlesFromTree();
    },
    extractAllArticlesFromTree() {
      const all = this.$root.contents?.articles || {};
      return Object.values(all)
        .filter(a => !a.deleted_at && a.versions && a.versions.length > 0)
        .map(a => ({ id: a.id, versions: a.versions }));
    },
    async loadCountdownSettings() {
      try {
        const response = await this.$root.$api.get('/questions/countdown-settings');
        if (response.data.settings) {
          this.countdownSettings = response.data.settings;
        }
      } catch (error) {
        console.error('Errore caricamento impostazioni countdown', error);
      }
    },
    async fetchStats() {
      this.isLoading = true;
      try {
        const params = {
          legal_expert_id: this.selectedExperts.join(','),
          article_ids: this.selectedArticlesIds.join(','),
          region_ids: this.filterRegion.join(','),
          year: this.selectedYear
        };
        if (this.selectedPeriodType === 'custom' && this.dateFrom && this.dateTo) {
          params.from = this.dateFrom;
          params.to = this.dateTo;
        }
        const res = await this.$root.$api.get('/statistics/stats-uoc', { params });
        const data = res.data;

        // KPI
        this.kpiTop = [
          { label: 'Nuovi quesiti', value: data.stats_quesiti.nuovi, bg: '#0dcaf0' },
          { label: 'Quesiti assegnati', value: data.stats_quesiti.assegnati, bg: '#ffc107' },
          { label: 'Quesiti asseverati', value: data.stats_quesiti.asseverati, bg: '#198754' },
          { label: 'Quesiti non asseverati', value: data.stats_quesiti.non_asseverati, bg: '#dc3545' },
          { label: 'Articolo rilevante', value: null, bg: '#6c757d', key: 'articolo' }
        ];

        // Tempi gestione
        this.tempiGestione = [
          { label: 'Tempo medio in sala d\'attesa', value: data.tempo_medio_sala_attesa_giorni, unit: 'giorni', max: 60, color: '#ffc107' },
          { label: 'Tempo medio in sala UOC', value: data.tempo_medio_sala_uoc_giorni, unit: 'giorni', max: 60, color: '#198754' },
          { label: 'Tempo medio di evasione', value: data.tempo_medio_evasione_giorni, unit: 'giorni', max: 60, color: '#0d6efd' }
        ];

        // Grafico
        this.articoloRilevanteId = data.stats_quesiti.articolo_rilevante;
        this.graficoLabels = data.grafico.labels || [];
        this.nuovi = data.grafico.nuovi || [];
        this.assegnati = data.grafico.assegnati || [];
        this.asseverati = data.grafico.asseverati || [];
        this.nonAsseverati = data.grafico.non_asseverati || [];

      } catch (err) {
        console.error('Errore fetchStats:', err);
      } finally {
        this.isLoading = false;
      }
    },
    getEchartsInstance() {
      return this.$refs.chartRef?.chart;
    },
    exportPNG() {
      const chart = this.getEchartsInstance();
      if (!chart) return;

      const url = chart.getDataURL({
        type: 'png',
        pixelRatio: 2,
        backgroundColor: '#fff'
      });

      const link = document.createElement('a');
      link.href = url;
      link.download = 'andamento_quesiti.png';
      link.click();
    },

    exportCSV() {
      let csv = 'ANDAMENTO QUESITI\nPeriodo;Nuovi;Assegnati;Asseverati;Non Asseverati\n';

      this.graficoLabels.forEach((l, i) => {
        csv += `${l};${this.nuovi[i] ?? 0};${this.assegnati[i] ?? 0};${this.asseverati[i] ?? 0};${this.nonAsseverati[i] ?? 0}\n`;
      });

      csv += '\nRIEPILOGO\nTipo;Valore\n';
      this.kpiTopComputed.forEach(k => {
        csv += `${k.label};${k.value}\n`;
      });

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      saveAs(blob, 'dashboard_quesiti.csv');
    },

    async exportExcelWithChart() {
      const chart = this.getEchartsInstance();
      if (!chart) return;

      const chartDataUrl = chart.getDataURL({
        type: 'png',
        pixelRatio: 2,
        backgroundColor: '#fff'
      });

      const base64 = chartDataUrl.split(',')[1];
      const buffer = Uint8Array.from(atob(base64), c => c.charCodeAt(0));

      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet('Statistiche');

      ws.addRow(['ANDAMENTO QUESITI']);
      ws.addRow(['Periodo', 'Nuovi', 'Assegnati', 'Asseverati', 'Non Asseverati']);

      this.graficoLabels.forEach((l, i) => {
        ws.addRow([
          l,
          this.nuovi[i] ?? 0,
          this.assegnati[i] ?? 0,
          this.asseverati[i] ?? 0,
          this.nonAsseverati[i] ?? 0
        ]);
      });

      ws.addRow([]);
      ws.addRow(['RIEPILOGO']);
      ws.addRow(['Tipo', 'Valore']);

      this.kpiTopComputed.forEach(k => {
        ws.addRow([k.label, k.value]);
      });

      const imageId = wb.addImage({ buffer, extension: 'png' });
      ws.addImage(imageId, {
        tl: { col: 6, row: 1 },
        ext: { width: 1000, height: 260 }
      });

      const wbout = await wb.xlsx.writeBuffer();
      saveAs(new Blob([wbout]), 'dashboard_quesiti.xlsx');
    }
  }
}
</script>


<style scoped>
.chart-toggle {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1px solid #0d6efd;
  color: #0d6efd;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  transition: all 0.2s ease-in-out, transform 0.15s ease-in-out;
}

.chart-toggle:hover {
  background-color: rgba(13, 110, 253, 0.1);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.chart-toggle.active {
  background-color: #0d6efd;
  color: #fff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  transform: translateY(-1px);
}

.chart-toggle.active i {
  transform: scale(1.1);
  transition: transform 0.15s ease-in-out;
}

.dropdown {
  min-width: 6rem;
}

/** .dropdown .dropdown-menu {
  min-width: 17rem;
  max-width: 20rem;
  width: auto;
}*/

.filter-dropdown .btn,
.filter-dropdown select.form-select {
  min-height: 2rem;
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
  line-height: 1.5;
  max-height: 2rem;
}

.filter-dropdown .dropdown-menu {
  max-height: 2rem;
  /* mantiene lo scroll verticale */
  overflow-y: auto;
  width: 220px;
  /* stessa larghezza massima per tutti */
}

.filter-btn {
  width: 32px;
  height: 32px;
  min-height: 2rem;
  padding: 0;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.article-btn {
  max-width: 220px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 2000;
}

.loading-text {
  margin-top: 1rem;
  font-weight: bold;
  color: #0d6efd;
}

.statistics-view .card .fw-bold {
  font-size: 1.3rem !important;
}
</style>
